import { useCallback, useMemo } from 'react';
import { ApiError, NetworkError } from '@cbhq/data-layer';
import type { WorkspaceMembersResult } from '@cbhq/identity-api-hooks/account-management/fetch';
import {
  useRefreshWorkspaceMembers,
  useWithOptimisticWorkspaceMembersUpdates,
} from '@cbhq/identity-api-hooks/account-management/fetch';
import {
  useAddOrganizationMember,
  useBulkCancelInvites,
  useRemoveOrganizationMember,
  useUpdateOrganizationMemberRole,
} from '@cbhq/identity-api-hooks/account-management/mutation';

import { useOrganization } from '../../contexts/OrganizationContext';
import { logError } from '../../utils/logging';

import type { MemberRole } from './utils/constants';
import type { LegalEntityRole } from './useMultiLEProductSelection';

type OptimisticData = Readonly<WorkspaceMembersResult | undefined>;
type MutationFn = (currentValue: OptimisticData) => [OptimisticData, Promise<unknown>];

function throwIfError(response: unknown) {
  if (response instanceof NetworkError) {
    throw new Error('Network error. Please check your connection.');
  }
  if (response instanceof ApiError) {
    throw new Error(response.message);
  }
}

type CancelInviteParams = {
  userId: string | undefined;
  email: string;
  legalEntityIds: string[];
};

type InviteMemberParams = {
  email: string;
  roles: MemberRole[];
  legalEntityIds: string[];
  legalEntityRoles?: LegalEntityRole[];
};

type UpdateMemberRolesParams = {
  memberUuid: string;
  selectedEntityIds: string[];
  roles: MemberRole[];
  getPartialErrorMessage: (entityNames: string) => string;
  getFullErrorMessage: () => string;
};

type UpdateMemberRolesPerEntityParams = {
  memberUuid: string;
  legalEntityRoles: LegalEntityRole[];
  getErrorMessage: () => string;
};

type RemoveMemberFromOrgParams = {
  memberUuid: string;
};

export function useTeamApi(organizationId: string) {
  const { selectedOrganization } = useOrganization();
  const withOptimisticWorkspaceMembersUpdates = useWithOptimisticWorkspaceMembersUpdates({
    organizationId,
  });
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  const bulkCancelInvites = useBulkCancelInvites();
  const addOrganizationMember = useAddOrganizationMember();
  const removeOrganizationMember = useRemoveOrganizationMember();
  const updateOrganizationMemberRole = useUpdateOrganizationMemberRole();

  const entityIdToName = useMemo(
    () =>
      new Map(
        (selectedOrganization?.legalEntities ?? []).map((le) => [
          le.legalEntityId,
          le.legalEntityName,
        ]),
      ),
    [selectedOrganization?.legalEntities],
  );

  // Wraps withOptimisticWorkspaceMembersUpdates with consistent error handling:
  // refreshes cache and logs on failure, then re-throws for component-level UI handling.
  const runWithOptimisticUpdate = useCallback(
    async (context: string, mutationFn: MutationFn) => {
      try {
        await withOptimisticWorkspaceMembersUpdates(mutationFn);
      } catch (err) {
        void refreshWorkspaceMembers();
        logError(err, { context, isHandled: true, isUserVisible: true });
        throw err;
      }
    },
    [withOptimisticWorkspaceMembersUpdates, refreshWorkspaceMembers],
  );

  const cancelInvite = useCallback(
    async ({ userId, email, legalEntityIds }: CancelInviteParams) => {
      await runWithOptimisticUpdate('TeamApi.cancelInvite', (currentData) => {
        const optimisticData = {
          ...currentData,
          pendingInvites: currentData?.pendingInvites?.filter((invite) => invite.email !== email),
        };

        const request = async () => {
          const response = await bulkCancelInvites({ userId, legalEntityIds });
          throwIfError(response);
          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, bulkCancelInvites],
  );

  const inviteMember = useCallback(
    async ({ email, roles, legalEntityIds, legalEntityRoles }: InviteMemberParams) => {
      await runWithOptimisticUpdate('TeamApi.inviteMember', (currentData) => {
        const legalEntityRolesById = new Map(
          (legalEntityRoles ?? []).map((legalEntityRole) => [
            legalEntityRole.legalEntityId,
            legalEntityRole.roles,
          ]),
        );
        const hasLegalEntityRoles = (legalEntityRoles?.length ?? 0) > 0;
        const optimisticLegalEntityIds = hasLegalEntityRoles
          ? legalEntityRoles?.map((legalEntityRole) => legalEntityRole.legalEntityId) ?? []
          : legalEntityIds;
        const requestLegalEntityIds = hasLegalEntityRoles ? [] : legalEntityIds;

        const optimisticInvites = optimisticLegalEntityIds.map((entityId) => ({
          roles: legalEntityRolesById.get(entityId) ?? roles,
          legalEntityId: entityId,
          createdAt: new Date().toISOString(),
        }));

        const optimisticData = {
          ...currentData,
          pendingInvites: [...(currentData?.pendingInvites ?? []), ...optimisticInvites],
        };

        const request = async () => {
          const response = await addOrganizationMember({
            member: { email, roles },
            legalEntityIds: requestLegalEntityIds,
            legalEntityRoles,
          });
          throwIfError(response);
          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, addOrganizationMember],
  );

  const updateMemberRoles = useCallback(
    async ({
      memberUuid,
      selectedEntityIds,
      roles,
      getPartialErrorMessage,
      getFullErrorMessage,
    }: UpdateMemberRolesParams) => {
      await runWithOptimisticUpdate('TeamApi.updateMemberRoles', (currentData) => {
        // Entities where all roles were removed should be treated as deletions, not updates.
        // Calling PUT /role with an empty roles array causes a backend error.
        const emptyRoleEntityIds = roles.length === 0 ? selectedEntityIds : [];
        const emptyRoleSet = new Set(emptyRoleEntityIds);

        const updatedMembers = currentData?.members?.map((m) => {
          if (m.userUuid !== memberUuid) return m;
          const currentLegalEntityRoles = m.legalEntityRoles ?? [];
          const nextLegalEntityRoles = selectedEntityIds
            .filter((entityId) => !emptyRoleSet.has(entityId))
            .map((entityId) => {
              const existingRole = currentLegalEntityRoles.find(
                (ler) => ler.legalEntityId === entityId,
              );
              const legalEntityName = existingRole?.legalEntityName ?? entityIdToName.get(entityId);
              return { ...existingRole, legalEntityId: entityId, legalEntityName, roles };
            });
          return { ...m, legalEntityRoles: nextLegalEntityRoles };
        });

        const member = currentData?.members?.find((m) => m.userUuid === memberUuid);
        const memberEntityIds = (member?.legalEntityRoles ?? [])
          .map((ler) => ler.legalEntityId ?? '')
          .filter(Boolean);
        const removedEntityIds = memberEntityIds.filter((id) => !selectedEntityIds.includes(id));
        const currentRolesByEntityId = new Map(
          (member?.legalEntityRoles ?? [])
            .filter((ler) => Boolean(ler.legalEntityId))
            .map((ler) => [ler.legalEntityId ?? '', (ler.roles ?? []) as MemberRole[]]),
        );

        const request = async () => {
          const entitiesToUpdate =
            roles.length === 0
              ? []
              : selectedEntityIds.filter((entityId) => {
                  const currentRoles = currentRolesByEntityId.get(entityId);
                  if (!currentRoles) return true;
                  if (currentRoles.length !== roles.length) return true;
                  const currentRolesSet = new Set(currentRoles);
                  return roles.some((role) => !currentRolesSet.has(role));
                });

          const updatePromises = entitiesToUpdate.map(async (entityId) => {
            const response = await updateOrganizationMemberRole({
              memberUuid,
              roles,
              legalEntityId: entityId,
            });
            throwIfError(response);
            return entityId;
          });

          // Remove membership from deselected entities and entities whose last role was removed.
          // If the last entity is removed, backend removes org membership as a result.
          const allRemovedEntityIds = [...removedEntityIds, ...emptyRoleEntityIds];
          const removePromises = allRemovedEntityIds.map(async (entityId) => {
            const response = await removeOrganizationMember({
              memberUuid,
              legalEntityId: entityId,
            });
            throwIfError(response);
            return entityId;
          });

          const updateResults = await Promise.allSettled(updatePromises);
          const removeResults = updateResults.every((r) => r.status === 'fulfilled')
            ? await Promise.allSettled(removePromises)
            : [];

          const changedEntityIds =
            removeResults.length > 0
              ? [...entitiesToUpdate, ...allRemovedEntityIds]
              : entitiesToUpdate;
          const allResults = [...updateResults, ...removeResults];
          const failedEntityIds = allResults
            .map((result, index) => (result.status === 'rejected' ? changedEntityIds[index] : null))
            .filter((id): id is string => id !== null);

          if (failedEntityIds.length > 0 && failedEntityIds.length < changedEntityIds.length) {
            const entityNames = failedEntityIds
              .map((id) => entityIdToName.get(id) ?? id)
              .join(', ');
            throw new Error(getPartialErrorMessage(entityNames));
          }

          if (failedEntityIds.length === changedEntityIds.length && changedEntityIds.length > 0) {
            throw new Error(getFullErrorMessage());
          }
        };

        return [{ ...currentData, members: updatedMembers }, request()];
      });
    },
    [
      runWithOptimisticUpdate,
      updateOrganizationMemberRole,
      removeOrganizationMember,
      entityIdToName,
    ],
  );

  const updateMemberRolesPerEntity = useCallback(
    async ({ memberUuid, legalEntityRoles, getErrorMessage }: UpdateMemberRolesPerEntityParams) => {
      await runWithOptimisticUpdate('TeamApi.updateMemberRolesPerEntity', (currentData) => {
        const rolesById = new Map(legalEntityRoles.map((ler) => [ler.legalEntityId, ler.roles]));

        const member = currentData?.members?.find((m) => m.userUuid === memberUuid);
        const currentRolesByEntityId = new Map(
          (member?.legalEntityRoles ?? [])
            .filter((ler) => Boolean(ler.legalEntityId))
            .map((ler) => [ler.legalEntityId ?? '', (ler.roles ?? []) as MemberRole[]]),
        );

        // LEs absent from the new list or present with empty roles should be removed.
        const newEntityIds = new Set(legalEntityRoles.map((ler) => ler.legalEntityId));
        const delistedEntityIds = [...currentRolesByEntityId.keys()].filter(
          (id) => !newEntityIds.has(id),
        );
        const emptyRoleEntityIds = legalEntityRoles
          .filter(({ roles }) => roles.length === 0)
          .map(({ legalEntityId }) => legalEntityId);
        const removedEntityIds = [...delistedEntityIds, ...emptyRoleEntityIds];

        const updatedMembers = currentData?.members?.map((m) => {
          if (m.userUuid !== memberUuid) return m;
          const removedSet = new Set(removedEntityIds);
          const nextLegalEntityRoles = (m.legalEntityRoles ?? [])
            .filter((ler) => !removedSet.has(ler.legalEntityId ?? ''))
            .map((ler) => {
              const newRoles = rolesById.get(ler.legalEntityId ?? '');
              return newRoles ? { ...ler, roles: newRoles } : ler;
            });
          return { ...m, legalEntityRoles: nextLegalEntityRoles };
        });

        const request = async () => {
          const entitiesToUpdate = legalEntityRoles.filter(({ legalEntityId, roles }) => {
            if (roles.length === 0) return false; // removals handled separately
            const currentRoles = currentRolesByEntityId.get(legalEntityId);
            if (!currentRoles) return true;
            if (currentRoles.length !== roles.length) return true;
            const currentRolesSet = new Set(currentRoles);
            return roles.some((role) => !currentRolesSet.has(role));
          });

          if (entitiesToUpdate.length === 0 && removedEntityIds.length === 0) {
            // The caller (form) should not invoke this with no actual changes —
            // log so we can spot stale form-validation bugs without surfacing to the user.
            logError(new Error('updateMemberRolesPerEntity called with no changes'), {
              context: 'TeamApi.updateMemberRolesPerEntity',
              isHandled: true,
              isUserVisible: false,
            });
            return;
          }

          try {
            // Update first — if this fails, don't proceed to removals so we don't
            // leave the backend in a partial state (LEs removed, roles not updated).
            if (entitiesToUpdate.length > 0) {
              const response = await updateOrganizationMemberRole({
                memberUuid,
                legalEntityRoles: entitiesToUpdate,
              });
              throwIfError(response);
            }

            await Promise.all(
              removedEntityIds.map(async (entityId) => {
                const response = await removeOrganizationMember({
                  memberUuid,
                  legalEntityId: entityId,
                });
                throwIfError(response);
              }),
            );
          } catch {
            throw new Error(getErrorMessage());
          }
        };

        return [{ ...currentData, members: updatedMembers }, request()];
      });
    },
    [runWithOptimisticUpdate, updateOrganizationMemberRole, removeOrganizationMember],
  );

  const removeMemberFromOrg = useCallback(
    async ({ memberUuid }: RemoveMemberFromOrgParams) => {
      await runWithOptimisticUpdate('TeamApi.removeMemberFromOrg', (currentData) => {
        const optimisticData = {
          ...currentData,
          members: currentData?.members?.filter((m) => m.userUuid !== memberUuid),
        };

        const request = async () => {
          const response = await removeOrganizationMember({ memberUuid, organizationId });
          throwIfError(response);
          return response;
        };

        return [optimisticData, request()];
      });
    },
    [runWithOptimisticUpdate, removeOrganizationMember, organizationId],
  );

  return {
    cancelInvite,
    inviteMember,
    updateMemberRoles,
    updateMemberRolesPerEntity,
    removeMemberFromOrg,
  };
}
